/**
 * Avatar — user image with fallback initials.
 * Subtle background, refined sizing.
 */

import { cn } from '@/lib/cn';
import type { WithClassName } from '@/types/common';

export interface AvatarProps extends WithClassName {
  src?: string;
  alt?: string;
  name?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

const sizeMap = {
  sm: 'h-7 w-7 text-xs',
  md: 'h-9 w-9 text-sm',
  lg: 'h-11 w-11 text-base',
  xl: 'h-14 w-14 text-lg',
} as const;

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function Avatar({ src, alt, name, size = 'md', className }: AvatarProps): JSX.Element {
  return (
    <div
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full overflow-hidden',
        'bg-neutral-100 text-neutral-500 font-medium tracking-tight',
        sizeMap[size],
        className,
      )}
    >
      {src ? (
        <img src={src} alt={alt || name || 'Avatar'} className="h-full w-full object-cover" />
      ) : name ? (
        <span aria-hidden="true">{getInitials(name)}</span>
      ) : (
        <span aria-hidden="true" className="text-neutral-400">
          ?
        </span>
      )}
    </div>
  );
}
